import * as React from "react";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
function traverseFiber(fiber, ascending, selector) {
  if (!fiber)
    return;
  if (selector(fiber) === true)
    return fiber;
  let child = ascending ? fiber.return : fiber.child;
  while (child) {
    const match = traverseFiber(child, ascending, selector);
    if (match)
      return match;
    child = child.sibling;
  }
}
function wrapContext(context) {
  try {
    return Object.defineProperties(context, {
      _currentRenderer: {
        get() {
          return null;
        },
        set() {
        }
      },
      _currentRenderer2: {
        get() {
          return null;
        },
        set() {
        }
      }
    });
  } catch (_) {
    return context;
  }
}
const FiberContext = wrapContext(React.createContext(null));
class FiberProvider extends React.Component {
  render() {
    return /* @__PURE__ */ React.createElement(FiberContext.Provider, {
      value: this._reactInternals
    }, this.props.children);
  }
}
const { ReactCurrentOwner, ReactCurrentDispatcher } = React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
function useFiber() {
  const root = React.useContext(FiberContext);
  if (!root)
    throw new Error("its-fine: useFiber must be called within a <FiberProvider />!");
  const id = React.useId();
  const fiber = React.useMemo(
    () => {
      var _a;
      return (_a = ReactCurrentOwner.current) != null ? _a : traverseFiber(root, false, (node) => {
        let state = node.memoizedState;
        while (state) {
          if (state.memoizedState === id)
            return true;
          state = state.next;
        }
      });
    },
    [root, id]
  );
  return fiber;
}
function useContainer() {
  const fiber = useFiber();
  const root = React.useMemo(
    () => traverseFiber(fiber, true, (node) => {
      var _a;
      return ((_a = node.stateNode) == null ? void 0 : _a.containerInfo) != null;
    }),
    [fiber]
  );
  return root == null ? void 0 : root.stateNode.containerInfo;
}
function useNearestChild(type) {
  const fiber = useFiber();
  const childRef = React.useRef();
  React.useLayoutEffect(() => {
    var _a;
    childRef.current = (_a = traverseFiber(
      fiber,
      false,
      (node) => typeof node.type === "string" && (type === void 0 || node.type === type)
    )) == null ? void 0 : _a.stateNode;
  }, [fiber]);
  return childRef;
}
function useNearestParent(type) {
  const fiber = useFiber();
  const parentRef = React.useRef();
  React.useLayoutEffect(() => {
    var _a;
    parentRef.current = (_a = traverseFiber(
      fiber,
      true,
      (node) => typeof node.type === "string" && (type === void 0 || node.type === type)
    )) == null ? void 0 : _a.stateNode;
  }, [fiber]);
  return parentRef;
}
const contexts = [];
const values = /* @__PURE__ */ new WeakMap();
function useContextBridge() {
  var _a;
  const fiber = useFiber();
  contexts.splice(0, contexts.length);
  traverseFiber(fiber, true, (node) => {
    var _a2;
    const context = (_a2 = node.type) == null ? void 0 : _a2._context;
    if (context && context !== FiberContext)
      contexts.push(wrapContext(context));
  });
  for (const context of contexts) {
    const value = (_a = ReactCurrentDispatcher.current) == null ? void 0 : _a.readContext(context);
    values.set(context, value);
  }
  return React.useMemo(
    () => contexts.reduce(
      (Prev, context) => (props) => /* @__PURE__ */ React.createElement(Prev, null, /* @__PURE__ */ React.createElement(context.Provider, __spreadProps(__spreadValues({}, props), {
        value: values.get(context)
      }))),
      (props) => /* @__PURE__ */ React.createElement(FiberProvider, __spreadValues({}, props))
    ),
    []
  );
}
export {
  FiberProvider,
  traverseFiber,
  useContainer,
  useContextBridge,
  useFiber,
  useNearestChild,
  useNearestParent
};
//# sourceMappingURL=index.js.map
